import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Paper, Divider, ClickAwayListener } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DropdownContainer = styled(Box)`
  position: relative;
  min-width: 120px;
  cursor: pointer;
`;

const DropdownButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background-color: white;
  border-radius: 50px;
  border: 1px solid #e0e0e0;
  height: 40px;
  box-sizing: border-box;
  font-size: 14px;
  color: #333;
  font-weight: 500;
  transition: border-color 0.2s ease;
  min-width: 140px;
  &:hover {
    border-color: #ccc;
  }
`;

const DropdownContent = styled(Paper)`
  position: absolute;
  top: 45px;
  left: 0;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 10px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${fadeIn} 0.25s ease;
  transform-origin: top center;
    
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #575758;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #F4762A;
  }
  
  /* Firefox */
  scrollbarWidth: thin;
  scrollbarColor: #575758 transparent;
`;

const DropdownItem = styled(ListItem)`
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  
  &:hover {
    background-color: #f5f5f5;
    color: #F4762A;
  }
`;

interface CustomDropdownProps {
  options: Array<{ id: string; value: string; label?: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string | number;
  firstOption?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'เลือก',
  width = 'auto',
  firstOption
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  const getDisplayLabel = () => {
    if (!value) return placeholder;
    
    if (firstOption && value === firstOption) return firstOption;
    
    const option = options.find(opt => 
      opt.value === value || opt.label === value
    );
    
    return option ? (option.label || option.value) : value;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={containerRef} sx={{ width }}>
      <DropdownButton onClick={toggleDropdown}>
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            fontSize: '16px',
          }}
        >
          {getDisplayLabel()}
        </Typography>
        {isOpen ? 
          <KeyboardArrowUpIcon sx={{ color: '#919192', ml: 1, transition: 'transform 0.2s ease' }} /> : 
          <KeyboardArrowDownIcon sx={{ color: '#919192', ml: 1, transition: 'transform 0.2s ease' }} />
        }
      </DropdownButton>

      {isOpen && (
        <DropdownContent>
          <List sx={{ padding: 0 }}>
            {firstOption && (
              <>
                <DropdownItem
                  onClick={() => handleSelect(firstOption)}
                  selected={value === firstOption}
                  sx={{
                    backgroundColor: value === firstOption ? '#f5f5f5' : 'inherit',
                    color: value === firstOption ? '#F4762A' : 'inherit',
                  }}
                >
                  <ListItemText primary={firstOption} />
                </DropdownItem>
                <Divider sx={{ margin: '0px 8px' }} />
              </>
            )}
            
            {options.map((option, index) => (
              <React.Fragment key={option.id || index}>
                {index > 0 && <Divider sx={{ margin: '0px 8px' }} />}
                <DropdownItem
                  onClick={() => handleSelect(option.label || option.value)}
                  selected={value === (option.label || option.value)}
                  sx={{
                    backgroundColor: value === (option.label || option.value) ? '#f5f5f5' : 'inherit',
                    color: value === (option.label || option.value) ? '#F4762A' : 'inherit',
                  }}
                >
                  <ListItemText primary={option.label || option.value} />
                </DropdownItem>
              </React.Fragment>
            ))}
          </List>
        </DropdownContent>
      )}
    </DropdownContainer>
  );
};

export default CustomDropdown; 